import { Stack, Typography, useTheme } from '@ethings/ui-toolkit';
import { Box, useMediaQuery } from '@mui/material';
import { FC, ReactNode, useMemo, type JSX } from 'react';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { PageIcon } from '../icons';

interface Props {
  'data-testid'?: string;
  icon?: JSX.Element;
  title: ReactNode;
}

export const PageTitle: FC<Props> = ({ icon, title, ...props }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-959px

  const textLength = innerText(title).length;

  const { variant, baseSize, maxLines, config } = useMemo<{
    variant: 'h4' | 'h5' | 'h6';
    baseSize: string;
    maxLines: number;
    config: { minFontSize: string; maxFontSize: string; textLengthThreshold: number };
  }>(() => {
    if (isSmallScreen) {
      const mobileVariant = textLength > 30 ? 'h6' : 'h5';
      const size = mobileVariant === 'h6' ? '1.125rem' : '1.25rem';
      return {
        variant: mobileVariant,
        baseSize: size,
        maxLines: 2,
        config: {
          minFontSize: '1rem',
          maxFontSize: size,
          textLengthThreshold: 30,
        },
      };
    }
    if (isMediumScreen) {
      const tabletVariant = textLength > 40 ? 'h5' : 'h4';
      const size = tabletVariant === 'h5' ? '1.25rem' : '1.5rem';
      return {
        variant: tabletVariant,
        baseSize: size,
        maxLines: 1,
        config: {
          minFontSize: '1.125rem',
          maxFontSize: size,
          textLengthThreshold: 40,
        },
      };
    }
    const desktopVariant = textLength > 40 ? 'h5' : 'h4';
    const size = desktopVariant === 'h5' ? '1.25rem' : '2.125rem';
    return {
      variant: desktopVariant,
      baseSize: size,
      maxLines: 1,
      config: {
        minFontSize: '1.25rem',
        maxFontSize: size,
        textLengthThreshold: 40,
      },
    };
  }, [isSmallScreen, isMediumScreen, textLength]);

  const responsiveStyles = useResponsiveText(baseSize, textLength, config);

  const titleStyles: React.CSSProperties = {
    fontSize: responsiveStyles.fontSize,
    lineHeight: responsiveStyles.lineHeight,
    wordBreak: responsiveStyles.wordBreak,
    whiteSpace: maxLines > 1 ? 'normal' : responsiveStyles.whiteSpace,
    overflow: responsiveStyles.overflow,
    textOverflow: responsiveStyles.textOverflow,
    ...(maxLines > 1 && {
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    } as React.CSSProperties),
  };

  return (<Stack direction="row" flexGrow={1} alignItems="center" spacing={2}>
    {icon && <Box display="flex" sx={{ alignContent: 'center' }}>
      <PageIcon icon={icon} />
    </Box>}
    <Box sx={{ width: 'auto', maxWidth: .9 }}>
      <Typography
        variant={variant}
        data-testid={props['data-testid']}
        style={titleStyles}
      >
        {title}
      </Typography>
    </Box>
  </Stack>);
};
